import { useEffect, useMemo, useState } from 'react'
import admin_hidden_api from '../api/admin/admin_hidden_valuations'
import org_api from '../api/admin/admin_orgs'
import report_api from '../api/admin/admin_report'
import api, {
	ROLE_ADMIN,
	ROLE_ORGANISATION_ADMIN,
	getRicsTypeId,
	isRequestCancelled,
	toDateRange,
} from '../api/finvise_reports_api'
import org_report_api from '../api/orgadmin/orgadmin_report'
import papi from '../api/pdf'
import profapi from '../api/profile'
import FinviseOrgFilter from '../components/FinviseOrgFilter'
import FinvisePageNavigation from '../components/FinvisePageNavigation'
import FinviseRangePicker from '../components/FinviseRangePicker'
import FinviseDialog from '../components/FinviseDialog'
import {
	defaultRange,
	fmtMoney,
	fmtShort,
	parse,
} from '../components/finviseMetrics'
import {
	filterProdOrganisations,
	restrictProdOrgIds,
} from '../utils/prodCompanyFilter'
import '../styles/finvise-portal.css'
import address_formatter from '../utils/Address'

const PAGE_SIZE = 8

// Every column is sized to fit its full header (headers wrap rather than
// truncate); the address column has no width so it takes all remaining space.
// Type is not a column: the page lists RICS reports only (see the note above
// the table).
const COLS = [
	{ key: 'ref', name: 'Reference', w: '92px' },
	{ key: 'instructed', name: 'Date instructed', w: '96px' },
	{ key: 'address', name: 'Property address' },
	{ key: 'postcode', name: 'Postcode', w: '86px' },
	{ key: 'valuer', name: 'Valuer', w: '96px' },
	{ key: 'teamMember', name: 'Team member', w: '96px' },
	{ key: 'valuation', name: 'Valuation', num: true, w: '94px' },
	{ key: 'status', name: 'Status', w: '104px' },
	{ key: 'query', name: 'Query', nosort: true, w: '64px' },
	{ key: 'report', name: 'Download', nosort: true, w: '88px' },
	{ key: 'archive', name: 'Archive', nosort: true, w: '76px' },
]

const DASH = '\u2014'
// statuses are stored as Submitted but shown to users as Ready
const STATUS_LABEL = { Submitted: 'Ready' }
// address works whether the row is mapped (address) or raw from the server (property_addr)
const addressOf = r => (r.address || r.property_addr || '').trim()

const FinviseProperties = ({
	orgId = null,
	role: roleProp = null,
	range: rangeProp = null,
	onRangeChange: onRangeChangeProp = null,
	orgFilter: orgFilterProp = null,
	onOrgFilterChange: onOrgFilterChangeProp = null,
} = {}) => {
	const [rows, setRows] = useState([])
	const [total, setTotal] = useState(null)
	const [loading, setLoading] = useState(false)
	const [role, setRole] = useState(roleProp)
	const [rangeState, setRangeState] = useState(defaultRange)
	// controlled when a parent lifts range state to keep Dashboard/Properties
	// on the same period, uncontrolled (own state) otherwise
	const range = rangeProp || rangeState
	const setRange = onRangeChangeProp || setRangeState
	const [searchInput, setSearchInput] = useState('')
	const [search, setSearch] = useState('')
	const [sortKey, setSortKey] = useState('instructed')
	const [sortDir, setSortDir] = useState(-1)
	const [page, setPage] = useState(1)
	const [showDownloading, setShowDownloading] = useState(false)
	const [showErrorDialog, setShowErrorDialog] = useState(false)
	const [error, setError] = useState()
	const [organisations, setOrganisations] = useState([])
	// controlled when a parent lifts the company filter so it survives
	// switching between Dashboard and Report Register
	const [orgFilterState, setOrgFilterState] = useState([])
	const orgFilter = orgFilterProp || orgFilterState
	const setOrgFilter = onOrgFilterChangeProp || setOrgFilterState
	const okButton = [{ text: 'OK', id: '1' }]

	// ORG admins (role 2) don't see the Valuer column (address stays for everyone)
	const hideValuer = role === ROLE_ORGANISATION_ADMIN
	// Archive is main-admin only — org admins never get an `archived` value
	// back from the server for a row, so the toggle wouldn't mean anything
	const showArchive = role === ROLE_ADMIN
	const visibleCols = useMemo(
		() =>
			COLS.filter(
				c =>
					!(c.key === 'valuer' && hideValuer) &&
					!(c.key === 'archive' && !showArchive),
			),
		[hideValuer, showArchive],
	)

	// main admin, viewing the cross-org console (no orgId fixed by the route)
	const showOrgFilter = role === ROLE_ADMIN && orgId == null

	const nowRef = useMemo(() => new Date(), [])
	const minDateRef = useMemo(() => new Date(2025, 0, 1), [])

	// resolve role_id: use the prop if provided, else fetch the profile
	useEffect(() => {
		if (roleProp != null) {
			setRole(roleProp)
			return
		}
		let alive = true
		profapi
			.getProfile()
			.then(r => alive && setRole(r && r.data ? r.data.role_id : 2))
			.catch(() => alive && setRole(2))
		return () => {
			alive = false
		}
	}, [roleProp])

	// debounce the search box → triggers a new server request
	useEffect(() => {
		const t = setTimeout(() => {
			setSearch(searchInput)
			setPage(1)
		}, 350)
		return () => clearTimeout(t)
	}, [searchInput])

	// main admin only: load the company list to populate the org filter
	useEffect(() => {
		if (!showOrgFilter) return
		let alive = true
		org_api
			.getOrganisations()
			.then(r => {
				if (alive)
					setOrganisations(
						filterProdOrganisations((r.data && r.data.organisations) || []),
					)
			})
			.catch(() => alive && setOrganisations([]))
		return () => {
			alive = false
		}
	}, [showOrgFilter])

	const onOrgFilterChange = ids => {
		setOrgFilter(ids)
		setPage(1)
	}

	// server request on any filter/paging change
	useEffect(() => {
		if (role == null) return
		const controller = new AbortController()
		setLoading(true)
		api
			.getReports({
				role,
				orgId,
				orgIds: showOrgFilter ? orgFilter : null,
				page,
				page_size: PAGE_SIZE,
				dateRange: toDateRange(range.from, range.to),
				search: search || null,
				sortKey,
				sortDir,
				signal: controller.signal,
			})
			.then(res => {
				setRows(res.report_details || [])
				setTotal(res.total)
				setLoading(false)
			})
			.catch(err => {
				// a newer filter/page change already aborted this request and
				// started its own fetch — leave its loading state alone
				if (isRequestCancelled(err)) return
				setLoading(false)
			})
		return () => controller.abort()
	}, [
		range,
		page,
		search,
		sortKey,
		sortDir,
		orgId,
		role,
		showOrgFilter,
		orgFilter,
	])

	const maxPage =
		total != null
			? Math.max(1, Math.ceil(total / PAGE_SIZE))
			: rows.length === PAGE_SIZE
			? page + 1
			: page

	const onSort = c => {
		if (c.nosort) return
		if (sortKey === c.key) setSortDir(d => d * -1)
		else {
			setSortKey(c.key)
			setSortDir(c.key === 'instructed' ? -1 : 1)
		}
		setPage(1)
	}

	const onRangeChange = r => {
		setRange(r)
		setPage(1)
	}

	const buildCsvFilter = async () => {
		const selected =
			showOrgFilter && orgFilter.length ? orgFilter : orgId ? [orgId] : null
		// PROD_COMPANY_FILTER
		const restricted = await restrictProdOrgIds(
			role === ROLE_ADMIN,
			orgId,
			selected,
		)
		// no allowed company left: ask for an id that matches nothing rather
		// than an empty list the backend can't filter on
		const ids = restricted ? (restricted.length ? restricted : [0]) : selected
		return getRicsTypeId().then(reportTypeId => ({
			filters: {
				date_range: [toDateRange(range.from, range.to)],
				time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				...(search ? { property: search } : {}),
				...(sortKey ? { sort_key: sortKey, sort_dir: sortDir } : {}),
				...(ids
					? {
							organisations: ids,
							...(ids.length === 1 ? { org_id: ids[0] } : {}),
					  }
					: {}),
				...(reportTypeId != null ? { report_types: [reportTypeId] } : {}),
			},
		}))
	}

	const handleDownloadCSV = () => {
		if (role == null) return
		buildCsvFilter().then(filter => {
			const json = JSON.stringify(filter)
			const response =
				role === ROLE_ADMIN
					? report_api.downloadCSV(json)
					: org_report_api.downloadCSV(json)

			const date = new Intl.DateTimeFormat('en-US', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
			}).format(Date.now())

			setShowDownloading(true)
			response
				.then(res => {
					const url = window.URL.createObjectURL(new Blob([res.data]))
					const link = document.createElement('a')
					link.href = url
					link.setAttribute(
						'download',
						'management-reports - ' + date.replace(',', '') + '.csv',
					)
					document.body.appendChild(link)
					link.click()
					link.remove()
				})
				.catch(() => {
					setError('Failed to download csv')
					setShowErrorDialog(true)
				})
				.finally(() => setShowDownloading(false))
		})
	}

	// Download a single report PDF — server request via papi.getReport(id)
	const handleDownloadReport = r => {
		if (!r || r.id == null) {
			setError('Failed to download report')
			setShowErrorDialog(true)
			return
		}
		setShowDownloading(true)
		papi
			.getReport(r.id)
			.then(res => {
				const url = window.URL.createObjectURL(new Blob([res.data]))
				const link = document.createElement('a')
				link.href = url
				const filename = address_formatter.getPdfFilename({
					address: addressOf(r),
					postcode: r.postcode,
				})
				link.setAttribute('download', filename)
				document.body.appendChild(link)
				link.click()
				link.remove()
			})
			.catch(() => {
				setError('Failed to download report')
				setShowErrorDialog(true)
			})
			.finally(() => setShowDownloading(false))
	}

	// Bulk-download every currently-downloadable report as one ZIP
	const downloadableIds = useMemo(
		() =>
			rows
				.filter(r => r.type === 'RICS' && r.status === 'Submitted')
				.map(r => r.id)
				.filter(id => id != null),
		[rows],
	)

	const handleDownloadAll = () => {
		if (!downloadableIds.length) return
		setShowDownloading(true)
		papi
			.getMultipleReports({ ids: downloadableIds })
			.then(res => {
				const url = window.URL.createObjectURL(
					new Blob([res.data], { type: 'application/zip' }),
				)
				const link = document.createElement('a')
				link.href = url
				link.setAttribute('download', 'reports.zip')
				document.body.appendChild(link)
				link.click()
				link.remove()
			})
			.catch(() => {
				setError('Failed to download reports')
				setShowErrorDialog(true)
			})
			.finally(() => setShowDownloading(false))
	}

	// Archive toggle — main admin only. Optimistic update with rollback on
	// failure, same pattern as AdminReportOverviewGrid's ArchiveToggle.
	const handleToggleArchive = r => {
		if (r.id == null) return
		const nextArchived = !r.archived
		setRows(prev =>
			prev.map(row =>
				row.id === r.id ? { ...row, archived: nextArchived } : row,
			),
		)
		admin_hidden_api
			.updateHiddenValuation(r.id, nextArchived ? 1 : 0)
			.catch(() => {
				setRows(prev =>
					prev.map(row =>
						row.id === r.id ? { ...row, archived: r.archived } : row,
					),
				)
				setError('Failed to update archive status')
				setShowErrorDialog(true)
			})
	}

	const handleOKDialogResult = () => setShowErrorDialog(false)

	const submittedInView = rows.filter(r => r.status === 'Submitted').length

	// ── one source of truth for a cell: header AND body both come from visibleCols ──
	const renderCell = (c, r) => {
		switch (c.key) {
			case 'ref':
				return (
					<span className='ref'>
						{r.ref || (r.pk != null ? '#' + r.pk : DASH)}
					</span>
				)
			case 'instructed': {
				const d = r.instructed ? parse(r.instructed) : null
				return d ? (
					<>
						{fmtShort(d)}
						<span className='cell-sub'>
							{String(d.getHours()).padStart(2, '0') +
								':' +
								String(d.getMinutes()).padStart(2, '0')}
						</span>
					</>
				) : (
					DASH
				)
			}
			case 'address':
				return <span className='wrap-text'>{addressOf(r) || DASH}</span>
			case 'postcode':
				return r.postcode || DASH
			case 'valuer':
				return <span className='wrap-text'>{r.valuer || DASH}</span>
			case 'teamMember':
				return <span className='wrap-text'>{r.teamMember || DASH}</span>
			case 'valuation':
				return r.valuation != null ? fmtMoney(r.valuation) : DASH
			case 'status':
				return (
					<span
						className={'status st-' + (r.status || '').replace(/\s/g, '')}
						title={
							r.status === 'Cancelled' && r.rejectionReason
								? r.rejectionReason
								: undefined
						}
					>
						<span className='dot' />
						{STATUS_LABEL[r.status] || r.status}
					</span>
				)
			case 'report':
				return r.type === 'RICS' && r.status === 'Submitted' ? (
					<button
						className='dl-btn'
						title='Download RICS report (PDF)'
						onClick={() => handleDownloadReport(r)}
					>
						<svg
							width='13'
							height='13'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						>
							<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
							<path d='M14 2v6h6M12 18v-6M9 15l3 3 3-3' />
						</svg>
					</button>
				) : (
					<span className='muted-cell'>{DASH}</span>
				)
			case 'query':
				return r.type === 'RICS' ? (
					<button
						className='dl-btn'
						title='Open the query thread for this report'
						onClick={() => window.location.assign('/profile/review/' + r.id)}
					>
						<svg
							width='13'
							height='13'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						>
							<path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
						</svg>
					</button>
				) : (
					<span className='muted-cell'>{DASH}</span>
				)
			case 'archive':
				return (
					<button
						className={'archive-toggle' + (r.archived ? ' on' : '')}
						title={r.archived ? 'Restore from archive' : 'Send to archive'}
						onClick={() => handleToggleArchive(r)}
					>
						<span className='archive-toggle-knob' />
					</button>
				)
			default:
				return r[c.key] != null ? r[c.key] : DASH
		}
	}

	const cellClassName = (c, r) => {
		const cls = []
		if (c.num) cls.push('num')
		if (c.key === 'address' && !addressOf(r)) cls.push('muted-cell')
		if (c.key === 'valuer' && !r.valuer) cls.push('muted-cell')
		if (c.key === 'teamMember' && !r.teamMember) cls.push('muted-cell')
		if (c.key === 'postcode' && !r.postcode) cls.push('muted-cell')
		if (c.key === 'valuation' && r.valuation == null) cls.push('muted-cell')
		return cls.length ? cls.join(' ') : undefined
	}

	const cellTitle = (c, r) => {
		if (c.key === 'address') return addressOf(r) || undefined
		if (c.key === 'valuer') return r.valuer || undefined
		if (c.key === 'teamMember') return r.teamMember || undefined
		return undefined
	}

	return (
		<div className='finvise-portal'>
			<div className='wrap'>
				<section className='page active' id='page-reg'>
					<div className='page-head'>
						<div>
							<h1>Report register</h1>
							<div className='sub sub-color'>
								All valuation reports instructed {fmtShort(range.from)} –{' '}
								{fmtShort(range.to)}
							</div>
						</div>
						<div className='head-actions'>
							{showOrgFilter ? (
								<FinviseOrgFilter
									organisations={organisations}
									selected={orgFilter}
									onChange={onOrgFilterChange}
								/>
							) : null}
							<FinviseRangePicker
								now={nowRef}
								minDate={minDateRef}
								value={range}
								onChange={onRangeChange}
							/>
							<button
								className='action-btn'
								onClick={handleDownloadCSV}
								title='Export the filtered reports as CSV'
							>
								<svg
									width='15'
									height='15'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								>
									<path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' />
								</svg>
								Export CSV
							</button>
							<button
								className='action-btn'
								onClick={handleDownloadAll}
								disabled={!downloadableIds.length}
								title='Download every downloadable report in the current filter as a ZIP'
							>
								<svg
									width='15'
									height='15'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								>
									<path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' />
								</svg>
								Download Reports ({downloadableIds.length})
							</button>
						</div>
					</div>

					<div className='reg-tools'>
						<div className='search'>
							<input
								type='text'
								placeholder='Search by property address or postcode…'
								value={searchInput}
								onChange={e => setSearchInput(e.target.value)}
							/>
						</div>
						<div className='reg-count'>
							{loading
								? 'Loading…'
								: total != null
								? total.toLocaleString('en-GB') + ' reports'
								: rows.length + ' on this page'}
						</div>
					</div>

					<div className='table-note'>*All reports shown are of type RICS.</div>

					<div className='card table-card'>
						{loading ? (
							<div className='card-loading'>
								<span className='spinner' />
							</div>
						) : null}
						<div className='table-scroll'>
							<table id='reg-table'>
								<colgroup>
									{visibleCols.map(c => (
										<col key={c.key} style={c.w ? { width: c.w } : null} />
									))}
								</colgroup>
								<thead>
									<tr>
										{visibleCols.map(c => (
											<th
												key={c.key}
												className={c.num ? 'num' : undefined}
												onClick={() => onSort(c)}
												style={c.nosort ? undefined : { cursor: 'pointer' }}
											>
												{c.name}
												{sortKey === c.key ? (
													<span className='arr'>
														{sortDir === 1 ? '\u25b2' : '\u25bc'}
													</span>
												) : null}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{rows.length === 0 ? (
										<tr>
											<td colSpan={visibleCols.length} className='empty-state'>
												{loading
													? 'Loading…'
													: 'No reports match the current filters'}
											</td>
										</tr>
									) : (
										rows.map((r, i) => (
											<tr key={i}>
												{visibleCols.map(c => (
													<td
														key={c.key}
														className={cellClassName(c, r)}
														title={cellTitle(c, r)}
													>
														{renderCell(c, r)}
													</td>
												))}
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
						<div className='table-foot'>
							<span>
								{submittedInView.toLocaleString('en-GB')} ready on this page
							</span>
						</div>
					</div>
					<div className='table-bottom-sign sub-color'>
						All valuation reports instructed {fmtShort(range.from)} –{' '}
						{fmtShort(range.to)}
					</div>

					<FinvisePageNavigation
						page={page}
						maxPage={maxPage}
						previousPage={() => setPage(p => Math.max(1, p - 1))}
						nextPage={() => setPage(p => Math.min(maxPage, p + 1))}
						numberPageClick={n => n && setPage(n)}
					/>
				</section>
			</div>

			{showDownloading ? (
				<FinviseDialog
					busy
					title='Download'
					text='Your report is being prepared'
				/>
			) : null}
			{showErrorDialog ? (
				<FinviseDialog
					title='Error'
					text={error}
					buttons={okButton}
					onButton={handleOKDialogResult}
				/>
			) : null}
		</div>
	)
}

export default FinviseProperties
